export const BE_DOMAIN = process.env.NEXT_PUBLIC_BE_DOMAIN ?? "www.pia.be";
export const LU_DOMAIN = process.env.NEXT_PUBLIC_LU_DOMAIN ?? "www.piagroup.lu";

/**
 * Apex, www, and env form (e.g. www.piagroup.lu, piagroup.lu).
 * next-intl matches `Host` exactly; a missing variant makes `fr-BE` win over `fr-LU`
 * for `/fr/...` (same URL prefix), which breaks `/fr/services` vs `/fr/pia-advisory` for LU.
 */
export const domainHostVariants = (domainEnv: string): string[] => {
  const d = domainEnv.trim().toLowerCase().replace(/:\d+$/, "");
  const base = d.replace(/^www\./, "");
  const withWww = `www.${base}`;
  return [...new Set([d, base, withWww])];
};

/** Staging / preview hosts (comma-separated). Default includes development.piagroup.lu. */
const LU_EXTRA_HOSTS = (process.env.NEXT_PUBLIC_LU_EXTRA_HOSTS ?? "development.piagroup.lu")
  .split(",")
  .map((h) => h.trim().toLowerCase())
  .filter(Boolean);

export const HOSTS = {
  be: domainHostVariants(BE_DOMAIN),
  lu: [...new Set([...domainHostVariants(LU_DOMAIN), ...LU_EXTRA_HOSTS])],
} satisfies { be: string[]; lu: string[] };

const hostKey = (host: string | null | undefined): string =>
  (host ?? "").trim().toLowerCase().replace(/:\d+$/, "");

export const isBeHost = (host: string | null | undefined): boolean =>
  HOSTS.be.includes(hostKey(host));

export const isLuHost = (host: string | null | undefined): boolean =>
  HOSTS.lu.includes(hostKey(host));

/** Public production domains only. Excludes staging hosts such as development.piagroup.lu. */
export const isPublicProductionHost = (host: string | null | undefined): boolean => {
  const key = hostKey(host);
  return domainHostVariants(BE_DOMAIN).includes(key) || domainHostVariants(LU_DOMAIN).includes(key);
};

// Strip port and www. prefix — only for sitemap URL comparison, not for HOSTS lookups.
export const normalizeHost = (host?: string | null): string => {
  if (!host) return "";
  return host
    .trim()
    .toLowerCase()
    .replace(/:\d+$/, "")
    .replace(/^www\./, "");
};
